import React, { useMemo, useRef } from 'react';
import { Trans } from '@lingui/react/macro';
import { FormGroup, Input } from 'src/components/ui';
import getPathFromId from '../../util/getPathFromId';
import { useJnx } from '../../util/jnx';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import useTestId from '../../hooks/useTestId';

function BoolYesNoField(props) {
  const {
    formData,
    registry,
    disabled,
    readonly: propReadonly,
    schema: { title },
    fieldPathId: { $id },
    uiSchema: {
      'akc:requiredIfVisible': requiredIfVisible,
      'ui:readonly': uiReadonly,
      'ui:onSelect': onSelectExpr,
      'ui:defaultValue': uiDefaultValue,
      'ui:hideTitle': propHideTitle,
      'ui:hideLabel': propHideLabel,
    },
    required: requiredProp,
    onChange: propOnChange,
  } = props;

  const uiHideTitle = useMemo(() => {
    if (propHideLabel != null) return propHideLabel;
    return propHideTitle ?? false;
  }, [propHideLabel, propHideTitle]);

  const formContext = registry?.formContext;
  const { setFormDataValues, sideChannel } = formContext;

  const readonly = uiReadonly || propReadonly;
  const required = !!requiredProp || !!requiredIfVisible;
  const testIdProps = useTestId($id);

  const map = { true: 1, false: 0 };

  const dataPath = useMemo(() => getPathFromId($id), [$id]);
  const functionBinds = useMemo(() => ({ set: setFormDataValues }), [setFormDataValues]);
  const onSelectJnx = useJnx(onSelectExpr, { functionBinds });
  const rootFormData = useSideChannelSubscription(sideChannel, 0);
  const rootFormDataRef = useRef();
  rootFormDataRef.current = rootFormData;

  function onChange({ target: { value } }) {
    const boolValue = value === '1';
    propOnChange(boolValue, dataPath);

    if (onSelectJnx) {
      setTimeout(() => {
        onSelectJnx.eval(rootFormDataRef.current, dataPath, {
          rootFormData: rootFormDataRef.current,
        });
      }, 500);
    }
  }

  return (
    <FormGroup disabled={readonly || disabled}>
      {title.trim() && !uiHideTitle ? (
        <label className="control-label" htmlFor={$id}>
          {title}
          {required ? <span className="required">*</span> : null}
        </label>
      ) : null}
      <div className="input-wrapper">
        <Input
          id={$id}
          {...testIdProps}
          type="select"
          disabled={readonly || disabled}
          required={required}
          value={formData in map ? map[formData] : uiDefaultValue in map ? map[uiDefaultValue] : 2}
          onChange={onChange}
        >
          <option value="2" disabled>
            ---
          </option>
          <option value="1">
            <Trans>Yes</Trans>
          </option>
          <option value="0">
            <Trans>No</Trans>
          </option>
        </Input>
      </div>
    </FormGroup>
  );
}

export default BoolYesNoField;
